import { ref, computed, watch } from "vue";
import { fabric } from "fabric";
import { store } from "@/store";

import { canvas } from "@/canvas-api/Canvas";
import { drawLine } from "@/canvas-api/CreateObjects";

const isRotated = computed<boolean>(() =>
  store.getters.canvasOrientation === "rotated" ? true : false
);

const bleedWidth = computed<number>(() => store.getters.bleedWidth);
const bleedHeight = computed<number>(() => store.getters.bleedHeight);
const safeZoneWidth = computed<number>(() => store.getters.safeZoneWidth);
const safeZoneHeight = computed<number>(() => store.getters.safeZoneHeight);

const viewPortPadding = 200; //100 top and bottom/left and right
export const viewPortWidth = computed<number>(() => store.getters.bleedWidth + viewPortPadding);
export const viewPortHeight = computed<number>(() => store.getters.bleedHeight + viewPortPadding);

const showGrid = computed<boolean>(() => store.getters.settingsState.grid);

watch(showGrid, () => {
  handleGridLines(canvas.value);
});

interface SnapLines {
  centerHoriz: fabric.Line;
  centerVert: fabric.Line;
  leftSafeZone: fabric.Line;
  rightSafeZone: fabric.Line;
  topSafeZone: fabric.Line;
  bottomSafeZone: fabric.Line;
  leftBleed: fabric.Line;
  rightBleed: fabric.Line;
  topBleed: fabric.Line;
  bottomBleed: fabric.Line;
}

export const snapLines = ref<SnapLines>({
  centerHoriz: new fabric.Line([0, 0, 0, 0]),
  centerVert: new fabric.Line([0, 0, 0, 0]),
  leftSafeZone: new fabric.Line([0, 0, 0, 0]),
  rightSafeZone: new fabric.Line([0, 0, 0, 0]),
  topSafeZone: new fabric.Line([0, 0, 0, 0]),
  bottomSafeZone: new fabric.Line([0, 0, 0, 0]),
  leftBleed: new fabric.Line([0, 0, 0, 0]),
  rightBleed: new fabric.Line([0, 0, 0, 0]),
  topBleed: new fabric.Line([0, 0, 0, 0]),
  bottomBleed: new fabric.Line([0, 0, 0, 0]),
});

export const handleGridLines = (canvas: fabric.Canvas) => {
  if (showGrid.value) {
    const safeZoneOffSetWidth: number = isRotated.value
      ? safeZoneHeight.value / 2
      : safeZoneWidth.value / 2;

    const safeZoneOffSetHeight: number = isRotated.value
      ? safeZoneWidth.value / 2
      : safeZoneHeight.value / 2;

    const bleedOffSetWidth: number = isRotated.value ? bleedHeight.value / 2 : bleedWidth.value / 2;

    const bleedOffSetHeight: number = isRotated.value
      ? bleedWidth.value / 2
      : bleedHeight.value / 2;

    const { left, top } = canvas.getCenter();

    const canvasWidth = canvas.getWidth() / canvas.getZoom();
    const canvasHeight = canvas.getHeight() / canvas.getZoom();

    const snapProps = {
      type: "line",
      id: "snapLines",
      name: "",
      originX: "center",
      originY: "center",
      stroke: "#00FF00",
      strokeWidth: 0.5,
      opacity: 0,
      objectCaching: false,
      selectable: false,
      evented: false,
      strokeDashArray: undefined,
    };

    snapProps.name = "horizontal";
    snapLines.value.centerHoriz = drawLine(
      [left - canvasWidth / 2, top, left + canvasWidth / 2, top],
      snapProps
    );
    snapLines.value.topSafeZone = drawLine(
      [
        left - canvasWidth / 2,
        top - safeZoneOffSetHeight,
        left + canvasWidth / 2,
        top - safeZoneOffSetHeight,
      ],
      snapProps
    );
    snapLines.value.bottomSafeZone = drawLine(
      [
        left - canvasWidth / 2,
        top + safeZoneOffSetHeight,
        left + canvasWidth / 2,
        top + safeZoneOffSetHeight,
      ],
      snapProps
    );
    snapLines.value.topBleed = drawLine(
      [
        left - canvasWidth / 2,
        top - bleedOffSetHeight,
        left + canvasWidth / 2,
        top - bleedOffSetHeight,
      ],
      snapProps
    );
    snapLines.value.bottomBleed = drawLine(
      [
        left - canvasWidth / 2,
        top + bleedOffSetHeight,
        left + canvasWidth / 2,
        top + bleedOffSetHeight,
      ],
      snapProps
    );

    snapProps.name = "vertical";
    snapLines.value.centerVert = drawLine(
      [left, top - canvasHeight / 2, left, top + canvasHeight / 2],
      snapProps
    );
    snapLines.value.leftSafeZone = drawLine(
      [
        left - safeZoneOffSetWidth,
        top - canvasHeight / 2,
        left - safeZoneOffSetWidth,
        top + canvasHeight / 2,
      ],
      snapProps
    );
    snapLines.value.rightSafeZone = drawLine(
      [
        left + safeZoneOffSetWidth,
        top - canvasHeight / 2,
        left + safeZoneOffSetWidth,
        top + canvasHeight / 2,
      ],
      snapProps
    );
    snapLines.value.leftBleed = drawLine(
      [
        left - bleedOffSetWidth,
        top - canvasHeight / 2,
        left - bleedOffSetWidth,
        top + canvasHeight / 2,
      ],
      snapProps
    );
    snapLines.value.rightBleed = drawLine(
      [
        left + bleedOffSetWidth,
        top - canvasHeight / 2,
        left + bleedOffSetWidth,
        top + canvasHeight / 2,
      ],
      snapProps
    );

    const gridLines = [
      snapLines.value.centerHoriz,
      snapLines.value.centerVert,
      snapLines.value.leftSafeZone,
      snapLines.value.rightSafeZone,
      snapLines.value.topSafeZone,
      snapLines.value.bottomSafeZone,
      snapLines.value.leftBleed,
      snapLines.value.rightBleed,
      snapLines.value.topBleed,
      snapLines.value.bottomBleed,
    ];

    const snapLineGroup = new fabric.Group(gridLines, {
      name: "snapLineGroup",
      objectCaching: false,
      selectable: false,
      evented: false,
    });

    canvas.add(snapLineGroup);
  } else {
    const canvasObjects = canvas.getObjects();

    let i = 0;
    while (canvasObjects[i]) {
      if (canvasObjects[i].name === "snapLineGroup") {
        canvas.remove(canvasObjects[i]);
      }

      i++;
    }
  }
};

export const setAllLinesOpaque = (canvas: fabric.Canvas) => {
  const snapGroup = canvas._objects.find((obj) => obj.name === "snapLineGroup") as fabric.Group;

  if (snapGroup) {
    snapGroup._objects.forEach((line: fabric.Object) => line.set("opacity", 0));
  }
};

export const resizeGridLines = (canvas: fabric.Canvas) => {
  const snapGroup = canvas._objects.find((obj) => obj.name === "snapLineGroup") as fabric.Group;

  const canvasWidth = canvas.getWidth() / canvas.getZoom();
  const canvasHeight = canvas.getHeight() / canvas.getZoom();

  if (snapGroup) {
    snapGroup._objects.forEach((obj: fabric.Object) => {
      const line = <fabric.Line>obj;

      if (line.name === "horizontal") {
        const diff = canvasWidth - line.getScaledWidth();

        line.set({
          width: line.getScaledWidth() + diff,
        });
      } else if (line.name === "vertical") {
        const diff = canvasHeight - line.getScaledHeight();

        line.set({
          height: line.getScaledHeight() + diff,
        });
      }
    });
  }

  canvas.renderAll();
};

const setPosition = (obj: fabric.Object, pos: string, value: number) => {
  if (pos === "left") {
    obj.set({
      left: value,
    });
  } else {
    obj.set({
      top: value,
    });
  }
};

const setOpacity = (line: fabric.Line, value: number) => {
  line.set({
    opacity: value,
  });
};

export const handleSnap = (fabricObj: fabric.Object) => {
  const snapTo = 5;
  const { x, y } = fabricObj.getCenterPoint();
  const objCoords: {
    bl: fabric.Point;
    br: fabric.Point;
    tl: fabric.Point;
    tr: fabric.Point;
  } = fabricObj.calcCoords(true);

  const centerHoriz = snapLines.value["centerHoriz"];
  if (Math.abs(y - centerHoriz.y1!) < snapTo) {
    fabricObj.set({
      top: centerHoriz.y1!,
    });

    setOpacity(centerHoriz, 1);
  } else if (centerHoriz.opacity === 1) {
    setOpacity(centerHoriz, 0);
  }

  const centerVert = snapLines.value["centerVert"];
  if (Math.abs(x - centerVert.x1!) < snapTo) {
    fabricObj.set({
      left: centerVert.x1!,
    });

    setOpacity(centerVert, 1);
  } else if (centerVert.opacity === 1) {
    setOpacity(centerVert, 0);
  }

  for (const snapLineKey of Object.keys(snapLines.value)) {
    const line = snapLines.value[snapLineKey as keyof SnapLines];

    if (line === centerVert || line === centerHoriz) {
      continue;
    }

    for (const coord of Object.values(objCoords)) {
      if (line.name === "vertical") {
        if (Math.abs(coord.x - line.x1!) < snapTo) {
          setPosition(fabricObj, "left", line.x1! + (x - coord.x));
          setOpacity(line, 1);
          break;
        } else if (line.opacity === 1) {
          setOpacity(line, 0);
        }
      } else {
        if (Math.abs(coord.y - line.y1!) < snapTo) {
          setPosition(fabricObj, "top", line.y1! + (y - coord.y));
          setOpacity(line, 1);
          break;
        } else if (line.opacity === 1) {
          setOpacity(line, 0);
        }
      }
    }
  }
};